import React, { useState } from "react";
import { useQuery } from "@apollo/client/react";
import { faArrowCircleLeft, faArrowCircleRight, faBan } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AnimationFileList, FolderSelector } from "@/components";
import Button from "@/components/button";
import { ButtonBar, Module } from "@/components/layouts";
import { ButtonGroup } from "@/components/layouts/button-group";
import { AnimationType } from "@/gql/generated/graphql";
import { MY_STUDIO } from "@/gql/queries/my-studio";
import { useModal } from "@/hooks/use-modal";

import ModalTitle from "./modal-title";

const ANIMATIONS_PER_PAGE = 6;

type LoadAnimationModalProps = {
  onSelected: (animationUrl: string) => void;
};

export default function LoadAnimationModal({
  onSelected,
}: LoadAnimationModalProps) {
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const { closeModal } = useModal();

  const onFolderChanged = (folderId: string | null) => {
    setCurrentPage(0);
    setCurrentFolderId(folderId);
  };

  const onAnimationSelected = (animationUrl: string) => {
    onSelected(animationUrl);
    closeModal();
  };

  const onPrevPage = () => {
    setCurrentPage(currentPage + 1);
  };
  const onNextPage = () => {
    setCurrentPage(currentPage - 1);
  };

  const {
    data = {
      privateAnimations: [],
      privateAnimationsCount: 0,
      animationFolders: [],
    },
  } = useQuery(MY_STUDIO, {
    variables: {
      offset: currentPage * ANIMATIONS_PER_PAGE,
      limit: ANIMATIONS_PER_PAGE,
      folderId: currentFolderId,
      types: [AnimationType.Standalone, AnimationType.Avatar],
    },
    fetchPolicy: "cache-and-network",
  });

  return (
    <div>
      <ModalTitle>Load Animation</ModalTitle>
      <ButtonBar>
        <ButtonGroup>
          <FolderSelector
            currentFolderId={currentFolderId}
            onFolderSelected={onFolderChanged}
          />
        </ButtonGroup>
        <ButtonGroup align="right">
          <Button
            onClick={onNextPage}
            disabled={currentPage === 0}
            icon={faArrowCircleLeft}
          >
            Newer
          </Button>
          <Button
            onClick={onPrevPage}
            disabled={data.privateAnimations.length < ANIMATIONS_PER_PAGE}
            icon={faArrowCircleRight}
          >
            Older
          </Button>
        </ButtonGroup>
      </ButtonBar>
      <Module>
        <AnimationFileList
          animations={data.privateAnimations}
          onAnimationSelected={onAnimationSelected}
        />
      </Module>
      <ButtonBar>
        <ButtonGroup align="right">
          <Button onClick={closeModal} type="negative">
            <FontAwesomeIcon icon={faBan} /> Cancel
          </Button>
        </ButtonGroup>
      </ButtonBar>
    </div>
  );
}
